/**
 * Houseplant v2 — Collection scroller scrollbar
 *
 * Drives sections/hp-collection-scroller.liquid. Scrolling itself stays native
 * (wheel, trackpad, swipe, keyboard); this only adds the visible drag rail the
 * design lacks, because a row that runs off-screen with no track and no arrows
 * is not discoverable on desktop — macOS hides its overlay scrollbar until you
 * are already scrolling, which is exactly the wrong time to learn you can.
 *
 * The thumb carries role="scrollbar", so it is also a real keyboard control:
 * arrows step, PageUp/PageDown page, Home/End jump.
 */
const STEP_RATIO = 0.25; // arrow key = a quarter of a viewport
const PAGE_RATIO = 0.9; // page key  = just under a full viewport, keeping context

class HpCollectionScroller extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.hp-collections__viewport');
    this.scrollbar = this.querySelector('.hp-collections__scrollbar');
    this.rail = this.querySelector('.hp-collections__rail');
    this.thumb = this.querySelector('.hp-collections__thumb');

    if (!this.viewport) return;

    this.onScroll = this.onScroll.bind(this);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

    /**
     * Observe the HOST, not the scroll port. Card widths are ems off the root
     * and the gutter tracks viewport width, so overflow can appear or vanish
     * without the element count changing — but the port is capped at 1440, so
     * at and above that width its box stops changing and the callback never
     * fires again. That is the whole range where `is-capped` has to flip and the
     * fade appears. The host always tracks the section width, so it changes on
     * every resize.
     */
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this);

    if (this.scrollbar && this.rail && this.thumb) this.bindScrollbar();

    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.viewport?.removeEventListener('scroll', this.onScroll);
    this.endDrag();
  }

  /* ------------------------------------------------------------- geometry */

  get maxScroll() {
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const { maxScroll } = this;

    /**
     * Right-edge fade. It exists only to soften the 1440 cap, so it is gated on
     * the cap actually binding — measured (port narrower than the section)
     * rather than guessed from a breakpoint, which would have to account for the
     * scrollbar's width. Below the cap the row runs off the viewport edge and
     * the design's hard bleed is correct; with nothing overflowing there is
     * nothing to fade either.
     */
    const capped = this.viewport.clientWidth < this.getBoundingClientRect().width - 1;
    this.classList.toggle('is-capped', capped && maxScroll > 1);

    if (!this.scrollbar) return;

    // A row that fits has nothing to drag — leave no dead control behind.
    this.scrollbar.hidden = maxScroll <= 1;
    if (this.scrollbar.hidden) return;

    const railWidth = this.rail.clientWidth;
    const ratio = this.viewport.clientWidth / this.viewport.scrollWidth;
    // A proportional thumb goes sub-pixel once a row is long enough to need one.
    const thumbWidth = Math.max(40, Math.round(railWidth * ratio));
    const progress = this.viewport.scrollLeft / maxScroll;

    this.thumbWidth = thumbWidth;
    this.railTravel = railWidth - thumbWidth;

    this.thumb.style.inlineSize = `${thumbWidth}px`;
    this.thumb.style.transform = `translateX(${this.railTravel * progress}px)`;
    this.thumb.setAttribute('aria-valuenow', Math.round(progress * 100));
  }

  scrollViewportTo(left, behavior = 'auto') {
    this.viewport.scrollTo({ left: Math.max(0, Math.min(left, this.maxScroll)), behavior });
  }

  /* ------------------------------------------------------------ scrollbar */

  bindScrollbar() {
    this.onPointerMove = this.onPointerMove.bind(this);
    this.endDrag = this.endDrag.bind(this);

    this.thumb.addEventListener('pointerdown', (event) => {
      event.preventDefault(); // don't let the pointer start a text selection
      this.dragOrigin = event.clientX;
      this.dragScrollLeft = this.viewport.scrollLeft;
      this.rail.classList.add('is-dragging');
      this.thumb.setPointerCapture(event.pointerId);
      this.thumb.addEventListener('pointermove', this.onPointerMove);
      this.thumb.addEventListener('pointerup', this.endDrag);
      this.thumb.addEventListener('pointercancel', this.endDrag);
    });

    // Clicking the bare rail pages towards the click, the way a native
    // scrollbar trough does — never a jump-to-position, which loses your place.
    this.rail.addEventListener('pointerdown', (event) => {
      if (event.target === this.thumb) return;
      const page = this.viewport.clientWidth * PAGE_RATIO;
      const thumbLeft = this.thumb.getBoundingClientRect().left;
      this.scrollViewportTo(this.viewport.scrollLeft + (event.clientX < thumbLeft ? -page : page), 'smooth');
    });

    this.thumb.addEventListener('keydown', this.onKeydown.bind(this));
  }

  onPointerMove(event) {
    if (this.dragOrigin == null || !this.railTravel) return;
    // Rail pixels → content pixels.
    const delta = ((event.clientX - this.dragOrigin) / this.railTravel) * this.maxScroll;
    this.scrollViewportTo(this.dragScrollLeft + delta);
  }

  endDrag() {
    this.dragOrigin = null;
    this.rail?.classList.remove('is-dragging');
    this.thumb?.removeEventListener('pointermove', this.onPointerMove);
    this.thumb?.removeEventListener('pointerup', this.endDrag);
    this.thumb?.removeEventListener('pointercancel', this.endDrag);
  }

  onKeydown(event) {
    const step = this.viewport.clientWidth * STEP_RATIO;
    const page = this.viewport.clientWidth * PAGE_RATIO;
    const from = this.viewport.scrollLeft;

    const moves = {
      ArrowLeft: from - step,
      ArrowRight: from + step,
      PageUp: from - page,
      PageDown: from + page,
      Home: 0,
      End: this.maxScroll,
    };

    if (!(event.key in moves)) return;
    event.preventDefault();
    this.scrollViewportTo(moves[event.key], 'smooth');
  }
}

if (!customElements.get('hp-collection-scroller')) {
  customElements.define('hp-collection-scroller', HpCollectionScroller);
}

/**
 * Theme editor: selecting a card block should bring it into view rather than
 * leaving the merchant looking at an unchanged row.
 */
document.addEventListener('shopify:block:select', (event) => {
  const scroller = event.target.closest?.('hp-collection-scroller');
  if (!scroller) return;

  const card = event.target.closest('.hp-collections__item');
  if (card) scroller.scrollViewportTo(card.offsetLeft - scroller.viewport.offsetLeft, 'smooth');
});
